import useUserHasActiveCoupon from "@customHooks/fc-coupon/user-has-active-coupon"
// import usePractitionerOrganizations from "@customHooks/organization/use-practitioner-organizations"
import usePractitionerCountry from "@customHooks/practitioner/use-practitioner-country"
import { IStripeSubscriptionPrice } from "@interfaces/fc-payment/subscriptions"
import { RootState } from "@redux/store"
import { getPlanPrice } from "@services/finance/fc-payment"
import { checkOrganizationSubscription } from "@services/finance/finance-services"
import { useCallback, useEffect, useState } from "react"
import { useSelector } from "react-redux"

/**
 * Retrieve the price subscription info
 * @param name PlanType Plan type. "COMPLETE" | "PREMIUM" | "LITE" | "FREE"
 * @param country Account country
 */
const useSubscriptionData = (name: string, country?: string) => {
    const { session, organization: organizationRedux } = useSelector((state: RootState) => ({ session:state.session, organization: state.organizations }));
    const { planPriceRedux} = useSelector((state: RootState) => ({ planPriceRedux:state.subscriptionSlice.planPrice }));
    const { organizations: organizationsRedux } = useSelector((state: RootState) => ({ organizations: state.relatedOrganization.organizationsM }));    
    // const { organizations, hasLoaded: hasLoadedOrganizations } = usePractitionerOrganizations("usesub", true )
    const [ searchCountry, setSearchCountry ] = useState<string | null>()
    const [ data, setData ] = useState<IStripeSubscriptionPrice | null>(null)
    const [ freeTrial, setFreeTrial ] = useState<boolean | null>(null)
    const [ hasActiveCoupon, setHasActiveCoupon ] = useState<boolean | null>(null)
    const [ organizationSub, setOrganizationSub ] = useState<boolean | null>(null)
    const [ error, setError ] = useState<unknown | null>(null)
    const [ isLoading, setIsLoading ] = useState<boolean>(false)

    const { practitionerCountry, hasLoaded: hasLoadedCountries } = usePractitionerCountry()

    const { data: activeCoupon, hasLoaded: hasLoadedActiveCoupon } = useUserHasActiveCoupon()

    /**
    * Function to check if the current subscription is a free trial
    */
    const verifyFreeTrial = useCallback(async () => {
        // if(!hasLoadedOrganizations) return
        setIsLoading(true)
        try 
        {
            console.debug("Verifing free trial")
            let baseOrganization
            if(organizationRedux.organizationId === 0 && organizationsRedux && organizationsRedux.length > 0)
            {
                baseOrganization = organizationsRedux[0]
                console.debug("Organization taken from API")
            }
            else
            {
                baseOrganization = organizationRedux
                console.debug("Organization taken from ds")
            }
            let subscriptionTypeData = session.subscriptionType
            if(!subscriptionTypeData){
                subscriptionTypeData = await checkOrganizationSubscription(baseOrganization.organizationId)
            }
            setFreeTrial(subscriptionTypeData?.type === "CUP")
            setOrganizationSub(subscriptionTypeData?.type === "SUB" && !organizationRedux.predetermined)
        } catch (error) {
            setFreeTrial(false)
            setOrganizationSub(false)
        } finally{
            setIsLoading(false)
        }
    }, [organizationRedux, organizationsRedux, session.subscriptionType])

    const getPlanPriceData = async (name:string, country: string) =>{
        try {
            const planPrice = getPlanPrice(name, country)
            return planPrice
        } catch (error) {
            return null
        }
    }

    useEffect(() => {
        const temp = country || practitionerCountry?.code || null
        setSearchCountry(temp)
    }, [country, hasLoadedCountries, practitionerCountry])

    useEffect(() => {
        if(!name) return
        if(name === "") return
        if(planPriceRedux){
            setData(planPriceRedux)
            return
        } 
        getPlanPrice(name, searchCountry)
            .then((res) => {
                setError(null)
                setData(res)
            })
            .catch(err => {
                console.error("getPlanPrice", err)
                setError(err)
                setData(null)
            })
    }, [name, searchCountry, planPriceRedux])

    useEffect(() => {
        verifyFreeTrial()
    }, [verifyFreeTrial])

    useEffect(() => {
        if(!hasLoadedActiveCoupon) return
        if(!(activeCoupon && activeCoupon.length > 0)) 
        {
            setHasActiveCoupon(false)
            return
        }

        const active = activeCoupon
            .filter(coupon => coupon.coupons.some(c => c.status === "used"))
            .length
        setHasActiveCoupon(active > 0)
    }, [ activeCoupon, hasLoadedActiveCoupon ])

    return { data, freeTrial, organizationSub, error, isLoading, country: searchCountry, hasActiveCoupon, getPlanPriceData:getPlanPriceData }
}

export default useSubscriptionData
